import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import LaserGunCard from "./business/LaserGunCard";
import QuantityDropDown from "./business/QuantityDropDown";
import {useTranslation} from "react-i18next";
import {gunOptionsInterface, StepProps, youthMinimumQuantity} from "../types/types";
import {beneluxCountries, getBookingConfig, itemPrice, logError, priceConfig} from "../data/price";
import {intersection} from 'lodash';
import DeliveryOptions from './business/DeliveryOptions';
import { changeGunOptions } from '../stateReducers/gunOptionSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import { isBow, isLaser } from './utils/stateHelper';
import { setFreeDeliverySwitch } from '../stateReducers/extraItemsSlice';


const Step1: FC<StepProps> = ({setStepValidity}) => {
    const d = useAppDispatch();
    const {t} = useTranslation();
    const [prices, setPrices] = useState<itemPrice[]>([]);

    const gunOptions = useAppSelector((state) => { return state.gunOptions} );
    const setGunOptions = (opts: gunOptionsInterface) => {
        d(changeGunOptions(opts));
    }
    const formType = useAppSelector((state) => { return state.delivery.formType })

    const checkStepIsValid = useCallback(() => {
        setStepValidity(gunOptions.gunType !== null && gunOptions.quantity !== null && gunOptions.quantity > 0);
    }, [gunOptions, setStepValidity]);

    //checks validity each time gunOptions is changed
    useEffect(() => {
        checkStepIsValid();
    }, [gunOptions, checkStepIsValid]);

    // Set the prices according to gunType
    useEffect(() => {
        if (gunOptions.gunType !== null) {
            let availableQuantities = priceConfig[gunOptions.gunType as keyof typeof priceConfig]
            if ("YouthPromo" === formType) {
                const minQuantity = bookingConfig.youthMinimumQuantity[gunOptions.gunType as keyof youthMinimumQuantity];
                console.log('filtering prices for YouthPromo min quantity', minQuantity);
                availableQuantities = availableQuantities.filter((priceConfig: itemPrice) => {
                    return priceConfig.quantity >= minQuantity;
                });
            }
            if (!beneluxCountries.includes(bookingConfig.country ?? 'be') && gunOptions.gunType === 'phoenix') {
                const maxQuantity = bookingConfig.maxPhoenixDpdQuantity;
                console.log('filtering prices for max phoenix DPD quantity for non-Benelux delivery', maxQuantity);
                availableQuantities = availableQuantities.filter((priceConfig: itemPrice) => {
                    return priceConfig.quantity <= maxQuantity;
                });
            }
            setPrices(availableQuantities);
        }
    }, [gunOptions.gunType]);

    // Selects gunType, sets quantity to 0, and sets prices according to gunType
    const selectLaserGun = (type: string) => {
        setGunOptions({quantity: 0, gunType: type});
        setPrices(priceConfig[type as keyof typeof priceConfig]);
        // logError('Selected lazerguns ' + type, gunOptions);
    }
    const setQuantity = (quantity: number) => {
        setGunOptions({...gunOptions, quantity: quantity});
        // logError('Selected quantity ' + quantity, gunOptions);
    }
    const bookingConfig = getBookingConfig();
    const country = bookingConfig['country'] ?? 'be';

    function countryCompatible(itemName: string) {
        if (country === 'be') {
            return true;
        } else {
            // Only phoenix is available outside Belgium.
            return itemName === 'phoenix';
        }
    }

    function displayItem(itemName: string) {
        switch (formType) {
            case 'LaserOnly':
                return isLaser(itemName) && countryCompatible(itemName);
            case 'BowOnly':
                return isBow(itemName) && countryCompatible(itemName);
            case 'Default':
                return true && countryCompatible(itemName);
            case 'YouthPromo':
                return true && countryCompatible(itemName);
        }
    }

    function getNumItems() {
        let items = ['phoenix', 'brx', 'childBow', 'adultBow'];
        return items.filter((item) => {
            return displayItem(item)
        }).length;
    };

    function getRowClass() {
        const items = getNumItems();
        if (items === 1) {
            return "card-grid-1";
        } else if (items === 2) {
            return "card-grid-2";
        } else if (items === 4) {
            return "card-grid-4";
        } else {
            return "";
        }
    };

    return (
        <div className="">
            <div className={"card-grid " + getRowClass()}>
                {(displayItem('phoenix')) &&
                    <LaserGunCard background="https://www.facetheaction.be/wp-content/uploads/2023/09/phoenix-cropped.jpg"
                                  onClick={() => selectLaserGun('phoenix')}
                                  selected={gunOptions.gunType === 'phoenix'}
                                  description={t('phoenix')}/>
                }
                {(displayItem('brx')) &&
                    <LaserGunCard background="https://www.facetheaction.be/wp-content/uploads/2023/09/BRX-cropped.jpg"
                                  onClick={() => selectLaserGun('brx')}
                                  selected={gunOptions.gunType === 'brx'}
                                  description={t('brx')}/>
                }
                {(displayItem('childBow')) &&
                    <LaserGunCard
                        background="https://www.archerytag.be/wp-content/uploads/2019/07/nieuwe-kinderbogen-2.jpg"
                        onClick={() => selectLaserGun('childBow')}
                        selected={gunOptions.gunType === 'childBow'}
                        description={t('childBow')}/>
                }
                {(displayItem('adultBow')) &&
                    <LaserGunCard
                        background="https://www.archerytag.be/wp-content/uploads/2018/03/archerytagboog-grijzerbg.jpg"
                        onClick={() => selectLaserGun('adultBow')}
                        selected={gunOptions.gunType === 'adultBow'}
                        description={t('adultBow')}/>
                }
            </div>
            <div>
                {gunOptions.gunType &&
                    <QuantityDropDown prices={prices}
                                      quantity={gunOptions.quantity}
                                      label={t('quantityDropdownLabel', {type: t(gunOptions.gunType + "Dropdown")})}
                                      onQuantityChange={setQuantity}
                                      itemType={gunOptions.gunType}/>
                }
                {gunOptions.gunType === 'phoenix' && <div className="p-2">
                    {t('phoenixNote')}
                </div>}
            </div>
            {!gunOptions.gunType &&
                <div className="p-2">{t('chooseLaserGunType')}</div>
            }
            <div>
                {gunOptions.gunType &&
                    <div className="p-2 whitespace-pre-line">{t('gunDescription.' + gunOptions.gunType)}</div>
                }
            </div>
        </div>
    );
};


export default Step1;
